import React from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import { useRegistrationData } from 'hooks/queries/users/useRegistrationData'
import { useBrandProfile } from 'hooks/queries/workspaces/useBrandProfile'
import { useOnboard } from 'hooks/queries/useOnboard'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useModals } from 'almanac-ui/Modal'
import { useFlow } from 'flows/common/hooks/useFlow'
import { onboardingSteps } from '../onboarding/steps'
import { LOGIN_PATH, SCHEDULED_POSTS_PATH } from 'paths'
import { vmRegistrationSteps } from 'screens/Registration/RegistrationAndOnboarding'
import { useSession } from 'hooks/useSession'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { useQueryClient } from 'react-query'
import { RegistrationData, UserClient } from 'api/clients/UserClient'

export const useRegistrationFlow = () => {
  const history = useHistory()
  const auth = useAuth()
  const hasAuth = Boolean(auth)
  const { logout } = useSession()
  const queryClient = useQueryClient()

  const { data: regResp } = useRegistrationData({ enabled: hasAuth })
  const { data: brandProfile } = useBrandProfile({ enabled: hasAuth })

  const { mutateAsync: onboard } = useOnboard()
  const currentWorkspaceId = useCurrentWorkspaceId()
  const { closeAllModals } = useModals()

  const computeStart = React.useCallback((): string => {
    if (!hasAuth) return 'registration'

    if (auth?.last_workspace_id === ALMANAC_CORE_WORKSPACE_ID || auth?.company_id === ALMANAC_CORE_WORKSPACE_ID) {
      logout({ logoutRedirect: LOGIN_PATH })
    }

    const reg = regResp?.user_registration_data ?? null

    if (!auth?.onboarded && !reg?.business_type) return 'businessType'
    if (!auth?.onboarded && reg?.marketing_experience_level == null) return 'marketingExperience'

    return 'businessType'
  }, [auth, hasAuth, logout, regResp])

  const steps = React.useMemo(() => [...vmRegistrationSteps, ...onboardingSteps], [])

  const handleComplete = async () => {
    await onboard()
    closeAllModals()

    const targetPath = generatePath(SCHEDULED_POSTS_PATH, { workspaceId: currentWorkspaceId, view: 'calendar' })
    history.push(targetPath)
  }

  const getOrFetch = <T>(key: any, fn: () => Promise<T>) => {
    const cached = queryClient.getQueryData<T>(key)
    return cached ? Promise.resolve(cached) : queryClient.fetchQuery(key, fn)
  }

  const flow = useFlow({
    id: 'fullSignupFlow',
    steps,
    fetchInitialData: async () => {
      if (!hasAuth) {
        return {
          auth: null,
          regData: null,
          initialState: 'registration',
        }
      }

      await getOrFetch('userRegistrationData', () => UserClient.getRegistrationData())

      return {
        auth,
        regData: queryClient.getQueryData('userRegistrationData') as RegistrationData | null,
        initialState: computeStart(),
      }
    },
    onComplete: handleComplete,
  })

  const resetFlow = React.useCallback(() => flow.send({ type: 'RESET' }), [flow])

  return { ...flow, resetFlow }
}
